import type { FlightOptionModelSpec } from '@elara/domain/models/FlightOptionModelSpec';

import type { FlightOptionsContainer } from '#containers/FlightOptionsContainer.js';
import type { ItineraryContainer } from '#containers/ItineraryContainer.js';
import type { ClientDocumentHandle, QueryHandle } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';
import type { FlightOptionModel } from '#models/FlightOptionModel.js';
import type { ItineraryItem } from '#models/ItineraryItemModel.js';

import { where } from '@elara/firebolt/shared';

import { trackBeginCheckout } from '#lib/analytics-events.js';
import { createState } from '#lib/signals-utils.js';

/**
 * Reactive state for the flight-checkout page.
 *
 * Keyed by `flightOptionId`. Subscribes directly to the matching flight
 * itinerary item (by `referenceId`) so the page works regardless of whether
 * the global trip context is set — booking state lives on that item's
 * `booking` field (there is no separate FlightBooking doc).
 */
export function createFlightCheckoutState(
  flows: AppFlowsContainer,
  flightOptions: FlightOptionsContainer,
  itinerary: ItineraryContainer,
) {
  return createState({
    flightHandle: undefined as ClientDocumentHandle<FlightOptionModelSpec> | undefined,
    itemsQuery: undefined as QueryHandle<ItineraryItem> | undefined,
    tripId: undefined as string | undefined,
    flightOptionId: undefined as string | undefined,
    isSubmitting: false,
    submitError: undefined as string | undefined,

    /** The matching flight itinerary item, if one exists. */
    get item(): ItineraryItem | undefined {
      const items = this.itemsQuery?.items;
      return items && items.length > 0 ? items[0] : undefined;
    },

    /** Convenience accessor — the booking subobject on the matching item. */
    get booking(): ItineraryItem['booking'] | undefined {
      return this.item?.booking;
    },

    get isReady(): boolean {
      return (this.flightHandle?.isReady ?? false) && (this.itemsQuery?.isReady ?? false);
    },

    get flightOption(): FlightOptionModel | undefined {
      return this.flightHandle?.data as FlightOptionModel | undefined;
    },

    /**
     * Open the checkout for a specific `flightOptionId`. Side-loads the
     * FlightOption doc and subscribes to the matching flight itinerary item
     * (filtered by `referenceId` + `type`).
     */
    open(tripId: string, flightOptionId: string): void {
      this.reset();
      this.tripId = tripId;
      this.flightOptionId = flightOptionId;
      trackBeginCheckout({ itemCategory: 'flight', tripId });
      this.flightHandle = flightOptions.ClientFlightOption.get({ tripId, id: flightOptionId });
      this.itemsQuery = itinerary.ClientItineraryItem.list(
        { tripId },
        where('type', '==', 'flight'),
        where('referenceId', '==', flightOptionId),
      ) as unknown as QueryHandle<ItineraryItem>;
    },

    reset(): void {
      this.flightHandle?.unsubscribe?.();
      this.itemsQuery?.unsubscribe?.();
      this.flightHandle = undefined;
      this.itemsQuery = undefined;
      this.tripId = undefined;
      this.flightOptionId = undefined;
      this.submitError = undefined;
    },

    /** Submit the user's external flight booking — writes onto the itinerary item. */
    async submit(input: Omit<Parameters<AppFlowsContainer['submitFlightBooking']>[0], 'tripId' | 'flightOptionId'>): Promise<void> {
      const tripId = this.tripId;
      const flightOptionId = this.flightOptionId;
      if (!tripId || !flightOptionId) {
        throw new Error('Flight checkout has not been opened.');
      }
      this.isSubmitting = true;
      this.submitError = undefined;
      try {
        await flows.submitFlightBooking({ tripId, flightOptionId, ...input });
      } catch (err) {
        this.submitError = err instanceof Error ? err.message : 'Failed to save booking';
        throw err;
      } finally {
        this.isSubmitting = false;
      }
    },

    async clear(): Promise<void> {
      const tripId = this.tripId;
      const flightOptionId = this.flightOptionId;
      if (!tripId || !flightOptionId) {
        throw new Error('Flight checkout has not been opened.');
      }
      await flows.clearFlightBooking({ tripId, flightOptionId });
    },
  });
}

export type FlightCheckoutState = ReturnType<typeof createFlightCheckoutState>;
